import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import { LayoutProvider, Page, Section } from 'react-page-layout';
import classnames from 'classnames';
import { connect } from 'react-redux';
import reduxUtils from 'util/redux';
import t from 'translations';
import $ from 'bianco.query';
import Aria51 from 'components/aria-51';
import { subscribe, unsubscribe } from 'util/misc/overlay-tracker';
import stopPropagation from 'util/dom/stop-propagation';
import Icon from 'components/icon';
import { numerals } from 'util/dom/mq';
import { MODAL_RESIZE_DEBOUNCE } from 'constants/debounce-delays';
import Loader from 'components/loader';


import SimpleModalLayout from './simple';
import ProductDetailModalLayout from './product-detail';
import ConfirmModalLayout from './confirm';
import BlankModalLayout from './blank';
import AlertModalLayout from './alert';
import ZipFormModalLayout from './zip-form';
import PreLaunchModalLayout from './prelaunch';
import IframeModalLayout from './iframe';

const layouts = {
    alert: AlertModalLayout,
    blank: BlankModalLayout,
    simple: SimpleModalLayout,
    confirm: ConfirmModalLayout,
    iframe: IframeModalLayout,
    'product-detail': ProductDetailModalLayout,
    'zip-form': ZipFormModalLayout,
    prelaunch: PreLaunchModalLayout,
};

export const HEIGHT_OFFSET = 88;

class Modal extends PureComponent {
    static defaultProps = {
        layout: 'simple',
    };

    static propTypes = {
        content: PropTypes.any,
        meta: PropTypes.any,
        isPanelOpen: PropTypes.bool.isRequired,
        viewportMqWidth: PropTypes.number.isRequired,
        isPendingRemove: PropTypes.bool.isRequired,
        layout: PropTypes.string.isRequired,
        viewportHeight: PropTypes.number.isRequired,
        closeModal: PropTypes.func.isRequired,
        resetModal: PropTypes.func.isRequired,
        isModalReady: PropTypes.bool.isRequired,
        scrollbarWidth: PropTypes.number.isRequired,
    };

    state = {
        isSticky: false,
        isLoaderVisible: true,
    };

    componentDidMount() {
        subscribe(this, this.props.closeModal);
        this.updateHeight();
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.isModalReady && !this.props.isModalReady) {
            this.updateHeightDebounced();
        }
    }

    componentDidUpdate() {
        this.updateHeightDebounced();
    }

    componentWillUnmount() {
        unsubscribe(this);
        this.updateHeightDebounced.cancel();
    }

    get isDesktop() {
        return this.props.viewportMqWidth > numerals.SM;
    }

    get isMobile() {
        return this.props.viewportMqWidth < numerals.SM;
    }

    get isTablet() {
        return this.props.viewportMqWidth === numerals.SM;
    }

    updateHeight = () => {
        // unfortunately we can't use refs in sub-templates so we query the DOM elements here
        const scroller = $('.modal--scroller', this.root)[0];
        const modalContent = $('.modal--content', this.refs.wrapper)[0];
        const offset = (this.isMobile ? 0 : HEIGHT_OFFSET);
        const newHeight = this.props.viewportHeight - offset;

        if (!scroller || !modalContent) {
            return;
        }

        const contentHeight = modalContent.offsetHeight;
        const isSticky = contentHeight > this.props.viewportHeight - offset;

        this.refs.wrapper.style.height = `${ isSticky && contentHeight > newHeight ? newHeight : contentHeight }px`;

        if (this.state.isSticky !== isSticky) {
            this.setState({ isSticky });
        }
    };

    getModalRoleAttributeBy(layout) {
        switch (layout) {
            case 'confirm':
            case 'alert':
                return 'alertdialog';
            case 'product-detail':
            case 'zip-form':
                return 'document';
            default:
                return 'dialog';
        }
    }


    updateHeightDebounced = debounce(function() {
        this.updateHeight();
    }, MODAL_RESIZE_DEBOUNCE);


    renderCloseButton() {
        const { layout, closeModal, scrollbarWidth } = this.props;
        const { isSticky } = this.state;

        if (layout === 'iframe') {
            return (
                <div className="modal__iframe--header">
                    <button className="modal--close-button"
                        role="button"
                        style={{ marginRight: isSticky ? scrollbarWidth : null }}
                        onClick={closeModal}
                        aria-label={t('accessibility.label.close_iframe_modal')}>
                        <Icon className='modal--close-button-icon' symbol="arrow-back" size="medium"/>
                        {t('modal.label.close_iframe_modal')}
                    </button>
                </div>
            );
        }
        return (
            <div className="modal--close-button"
                role="button"
                style={{ marginRight: isSticky ? scrollbarWidth : null }}
                onClick={closeModal}>
                <button aria-label={t('accessibility.label.close_modal')}>
                    <Icon symbol="cancel" size="medium"/>
                </button>
            </div>
        );
    }

    render() {
        const { layout, content, meta, closeModal, resetModal, isPendingRemove, scrollbarWidth, isModalReady } = this.props;
        const { isSticky, isLoaderVisible } = this.state;

        return (
            <Aria51
                getRoot={root => {
                    this.root = root;
                }}
                onClick={closeModal}
                tabIndex={0}
                aria-modal="true"
                role={this.getModalRoleAttributeBy(layout)}
                className={classnames('modal', `modal__${ layout }`, {
                    'modal__is-closing': isPendingRemove,
                    'modal__is-sticky': isSticky,
                    'modal__is-ready': isModalReady,
                })}>
                {isLoaderVisible &&
                <div onTransitionEnd={() => isModalReady && this.setState({ isLoaderVisible: false })}
                    className={classnames('modal--loader', {
                        'modal--loader__is-visible': !isModalReady,
                    })}><Loader/></div>}
                <div ref="wrapper"
                    className="modal--wrap"
                    onClick={stopPropagation}
                    onAnimationEnd={() => isPendingRemove && resetModal()}>
                    {this.renderCloseButton()}
                    <LayoutProvider layouts={layouts}>
                        <Page isDesktop={this.isDesktop}
                            scrollbarWidth={scrollbarWidth}
                            updateHeight={this.updateHeight}
                            isSticky={this.state.isSticky}
                            layout={layout}
                            meta={meta}
                            content={content}>
                            <Section slot="content">
                                {content}
                            </Section>
                        </Page>
                    </LayoutProvider>
                </div>
            </Aria51>
        );
    }
}

export default connect(
    reduxUtils.mapStateToProps({
        viewportMqWidth: 'viewport.mq.width',
        isPanelOpen: 'panel.isOpen',
        viewportHeight: 'viewport.height',
        isPendingRemove: 'modal.isPendingRemove',
        isModalReady: 'modal.isReady',
        scrollbarWidth: 'scroll.scrollbarWidth',
    }),
    reduxUtils.mapDispatchToProps({
        closeModal: 'modal.close',
        resetModal: 'modal.reset',
    }),
)(Modal);



// WEBPACK FOOTER //
// ./mcart/assets/src/js/app/components/modal/index.js